import React, { useState } from 'react';
import '../styles/PhaseResourceViewer.css';
import TrainingModuleViewer from './TrainingModuleViewer';
import { trainingModules } from '../data/trainingModules';

export function PhaseResourceViewer({ phase }) {
  const [activeResource, setActiveResource] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedModule, setSelectedModule] = useState(null);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const downloadChecklist = () => {
    let content = `Phase ${phase.order}: ${phase.name}\n`;
    content += `Duration: ${phase.duration}\n\n`;

    phase.checklist.forEach(category => {
      content += `${category.category}\n`;
      content += '='.repeat(category.category.length) + '\n';
      category.items.forEach(item => {
        content += `☐ ${item.task}\n`;
      });
      content += '\n';
    });

    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(content));
    element.setAttribute('download', `Phase-${phase.order}-Checklist.txt`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const downloadTemplate = (template) => {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(template.content));
    element.setAttribute('download', `${template.name.replace(/\s+/g, '-')}.txt`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  if (selectedModule) {
    return (
      <TrainingModuleViewer
        module={selectedModule}
        onClose={() => setSelectedModule(null)}
      />
    );
  }

  return (
    <div className="phase-resource-viewer">
      {/* Resource Tabs */}
      <div className="resource-tabs">
        <button
          className={`tab ${activeResource === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveResource('overview')}
        >
          📋 Overview
        </button>
        <button
          className={`tab ${activeResource === 'checklist' ? 'active' : ''}`}
          onClick={() => setActiveResource('checklist')}
        >
          ✓ Checklist
        </button>
        <button
          className={`tab ${activeResource === 'guide' ? 'active' : ''}`}
          onClick={() => setActiveResource('guide')}
        >
          📚 Implementation Guide
        </button>
        <button
          className={`tab ${activeResource === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveResource('templates')}
        >
          📊 Templates
        </button>
        <button
          className={`tab ${activeResource === 'training' ? 'active' : ''}`}
          onClick={() => setActiveResource('training')}
        >
          🎓 Training Modules
        </button>
      </div>

      {/* Overview Tab */}
      {activeResource === 'overview' && (
        <div className="resource-content overview-content">
          <div className="overview-header">
            <h2>Phase {phase.order}: {phase.name}</h2>
            <p className="duration">Duration: {phase.duration}</p>
          </div>
          <div className="overview-description">
            <p>{phase.overview}</p>
          </div>
        </div>
      )}

      {/* Checklist Tab */}
      {activeResource === 'checklist' && (
        <div className="resource-content checklist-content">
          <div className="checklist-header">
            <h2>Phase {phase.order} Checklist</h2>
            <button className="download-btn" onClick={downloadChecklist}>
              ⬇️ Download Checklist
            </button>
          </div>

          {phase.checklist.map((category, idx) => (
            <div key={idx} className="checklist-category">
              <h3 className="category-title">{category.category}</h3>
              <ul className="checklist-items">
                {category.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="checklist-item">
                    <input
                      type="checkbox"
                      defaultChecked={item.completed}
                      id={`item-${idx}-${itemIdx}`}
                    />
                    <label htmlFor={`item-${idx}-${itemIdx}`}>
                      {item.task}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Implementation Guide Tab */}
      {activeResource === 'guide' && (
        <div className="resource-content guide-content">
          <h2>Implementation Guide</h2>
          <p className="guide-intro">Step-by-step guidance for implementing this phase</p>

          {phase.implementationGuide.sections.map((section, idx) => (
            <div key={idx} className="guide-section">
              <button
                className="guide-section-header"
                onClick={() => toggleSection(`guide-${idx}`)}
              >
                <span className="section-title">{section.title}</span>
                <span className="duration-badge">{section.duration}</span>
                <span className="toggle-icon">
                  {expandedSections[`guide-${idx}`] ? '▼' : '▶'}
                </span>
              </button>

              {expandedSections[`guide-${idx}`] && (
                <div className="section-content">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Templates Tab */}
      {activeResource === 'templates' && (
        <div className="resource-content templates-content">
          <h2>Templates & Examples</h2>
          <p className="templates-intro">Ready-to-use templates to accelerate your implementation</p>

          <div className="templates-grid">
            {phase.templates.map((template, idx) => (
              <div key={idx} className="template-card">
                <div className="template-header">
                  <h3>{template.name}</h3>
                  <button
                    className="download-btn small"
                    onClick={() => downloadTemplate(template)}
                    title="Download as text file"
                  >
                    ⬇️
                  </button>
                </div>
                <p className="template-description">{template.description}</p>

                <button
                  className="view-template-btn"
                  onClick={() => toggleSection(`template-${idx}`)}
                >
                  {expandedSections[`template-${idx}`] ? 'Hide Template' : 'View Template'}
                </button>

                {expandedSections[`template-${idx}`] && (
                  <div className="template-preview">
                    <pre>{template.content}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Training Modules Tab */}
      {activeResource === 'training' && (
        <div className="resource-content training-content">
          <h2>Training Modules</h2>
          <p className="training-intro">Structured training to ensure successful implementation</p>

          <div className="training-modules">
            {phase.trainingModules.map((moduleData, idx) => {
              const fullModule = trainingModules[moduleData.id || `module-${phase.order}-${idx + 1}`];
              return (
                <div key={idx} className="training-module">
                  <div className="module-header">
                    <h3>{moduleData.title}</h3>
                    <span className="module-duration">⏱️ {moduleData.duration}</span>
                  </div>
                  <div className="module-topics">
                    <p className="topics-label">Topics covered:</p>
                    <ul>
                      {moduleData.topics.map((topic, topicIdx) => (
                        <li key={topicIdx}>{topic}</li>
                      ))}
                    </ul>
                  </div>
                  {fullModule && (
                    <button
                      className="launch-btn"
                      onClick={() => setSelectedModule(fullModule)}
                    >
                      🎓 Launch Module →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default PhaseResourceViewer;
